/*
 * Copyright (c) 2026 Dobler Data Solutions, LLC
 * Fabric Control Plane - Metadata Explorer
 * Feature #183: External Metadata Enrichment
 * Constitution: v1.18.0
 * Conformed to Spec 197 design blueprint
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Database, Table2, GitBranch, RefreshCw, Clock, Copy, Unlink } from 'lucide-react';
import { useMetadataOverview } from '../../services/operations/metadataService';
import { apiClient } from '../../services/apiClient';
import {
  PageLayout,
  Card,
  DataTable,
  StatCard,
  EmptyState,
  ErrorState,
  LoadingState,
  NotCollected,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';

interface MetadataInsightsPayload {
  kpis: {
    modelsWithMeasures: number;
    lineageEdgeCount: number;
    datasourceCensusByType: Array<{ type: string; count: number }>;
    rlsBearingModelsCount: number | null;
  };
  duplicateMeasures: Array<{ daxExpression: string; occurrences: Array<{ modelId: string; modelName: string; tableName: string; measureName: string }> }>;
  totalDuplicateGroups: number;
  unreferencedDatasources: Array<{ datasourceId: string; type: string; server: string | null; modelName: string }>;
  gatewayBlastRadius: Array<{ gatewayId: string; gatewayName: string; datasourceCount: number }>;
  refreshDataAvailable: boolean;
}

export function MetadataCrossConnectionsSection() {
  const { data } = useQuery<MetadataInsightsPayload>({
    queryKey: ['metadata-insights'],
    queryFn: async () => {
      const res = await apiClient.get<{ success: boolean; data: MetadataInsightsPayload }>('/api/metadata-insights');
      return res.data.data;
    },
    staleTime: 60_000,
  });

  if (!data) return null;

  const dupColumns: DataTableColumn<MetadataInsightsPayload['duplicateMeasures'][number]>[] = [
    { key: 'daxExpression', header: 'DAX Expression', width: '40%', render: (r) => <span className="font-mono text-xs">{r.daxExpression.slice(0, 80)}{r.daxExpression.length > 80 ? '…' : ''}</span> },
    { key: 'occurrences', header: 'Models', width: '60%', render: (r) => <span className="text-xs text-[var(--color-text-secondary)]">{r.occurrences.length} models: {r.occurrences.slice(0, 3).map((o) => o.modelName).join(', ')}{r.occurrences.length > 3 ? '…' : ''}</span> },
  ];

  const unreferencedColumns: DataTableColumn<MetadataInsightsPayload['unreferencedDatasources'][number]>[] = [
    { key: 'modelName', header: 'Model', width: '40%', cellClassName: 'font-medium text-[var(--color-text-primary)]' },
    { key: 'type', header: 'Type', width: '30%' },
    { key: 'server', header: 'Server', width: '30%', render: (r) => r.server ?? '—' },
  ];

  return (
    <div className="mt-6 space-y-6">
      <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Cross-connections</h2>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Models with Measures" value={data.kpis.modelsWithMeasures} icon={<Table2 className="h-4 w-4" />} />
        <StatCard label="Lineage Edges" value={data.kpis.lineageEdgeCount} icon={<GitBranch className="h-4 w-4" />} />
        <StatCard label="Duplicate DAX Groups" value={data.totalDuplicateGroups} icon={<Copy className="h-4 w-4" />} />
        <StatCard label="Unreferenced Datasources" value={data.unreferencedDatasources.length} subtext="on the latest collection" icon={<Unlink className="h-4 w-4" />} />
      </div>

      <Card padding="none">
        <h3 className="px-4 pt-4 text-sm font-semibold text-[var(--color-text-primary)]">Cross-workspace duplicate measures</h3>
        <p className="px-4 text-xs text-[var(--color-text-secondary)]">Same DAX text in multiple models — showing top {data.duplicateMeasures.length} of {data.totalDuplicateGroups} groups</p>
        <DataTable columns={dupColumns} rows={data.duplicateMeasures} rowKey={(r) => r.daxExpression} pageSize={10} emptyMessage="No duplicate measures detected" />
      </Card>

      <Card padding="none">
        <h3 className="px-4 pt-4 text-sm font-semibold text-[var(--color-text-primary)]">Unreferenced datasources</h3>
        <p className="px-4 text-xs text-[var(--color-text-secondary)]">Collected datasources with no lineage edge pointing at the owning model — on the latest collection</p>
        <DataTable columns={unreferencedColumns} rows={data.unreferencedDatasources} rowKey={(r) => r.datasourceId} pageSize={10} emptyMessage="No unreferenced datasources" />
      </Card>

      {!data.refreshDataAvailable && (
        <Card>
          <h3 className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">Refresh-based panels</h3>
          <NotCollected reason="No refresh history/schedules collected in this environment (DirectLake-only dev) — proves in prod" />
        </Card>
      )}
    </div>
  );
}

export const MetadataExplorerPage: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useMetadataOverview();

  const sm = data?.semanticModels;
  const schedules = data?.schedules;

  return (
    <PageLayout
      helpId="auto.metadata-explorer"
      title="Metadata Explorer"
      description="Semantic model definitions, lineage, refresh history, and item schedules collected via Scanner API."
      icon={<Database className="h-6 w-6" />}
      onRefresh={() => { void refetch(); }}
      isRefreshing={isLoading}
    >
      {isLoading ? (
        <Card>
          <LoadingState rows={6} />
        </Card>
      ) : isError ? (
        <ErrorState
          title="Failed to load metadata"
          message="The metadata overview could not be loaded. Please try again."
          onRetry={() => void refetch()}
        />
      ) : !sm || sm.total === 0 ? (
        <EmptyState
          icon={<Database className="h-10 w-10" />}
          title="No metadata collected yet"
          message="Run the daily collection tier to populate semantic model definitions, refresh history, and item schedules."
        />
      ) : (
        <>
          {/* Summary tiles */}
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard
              label="Semantic Models"
              value={sm.total}
              icon={<Database className="h-4 w-4" />}
            />
            <StatCard
              label="With Definition"
              value={sm.withDefinition}
              valueClassName="text-[var(--color-status-success)]"
              icon={<Table2 className="h-4 w-4" />}
            />
            <StatCard
              label="With Refresh History"
              value={sm.withRefreshHistory}
              icon={<RefreshCw className="h-4 w-4" />}
            />
            <StatCard
              label="Items with Schedules"
              value={schedules?.itemsWithSchedule ?? 0}
              icon={<Clock className="h-4 w-4" />}
            />
          </div>

          {/* Navigation tiles */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <NavigationTile
              title="Semantic Model Definitions"
              description="Tables, columns, measures, relationships, and RLS roles."
              icon={<Database className="h-8 w-8 text-[var(--color-accent)]" />}
              count={sm.withDefinition}
              countLabel="models with definition"
              onClick={() => navigate('/metadata/semantic-models')}
            />
            <NavigationTile
              title="Item Lineage"
              description="Upstream datasource and cross-item lineage links from Scanner API."
              icon={<GitBranch className="h-8 w-8 text-[var(--color-status-success)]" />}
              count={data?.lineage?.itemsWithLineage ?? 0}
              countLabel="items with lineage"
              onClick={() => navigate('/metadata/lineage')}
            />
            <NavigationTile
              title="Refresh History & Schedules"
              description="Per-dataset refresh run log and configured refresh schedules."
              icon={<RefreshCw className="h-8 w-8 text-[var(--color-status-info)]" />}
              count={sm.withRefreshHistory}
              countLabel="models with history"
              onClick={() => navigate('/metadata/refresh')}
            />
            <NavigationTile
              title="Item Schedules"
              description="Pipeline, notebook, and dataflow job schedules."
              icon={<Clock className="h-8 w-8 text-[var(--color-status-warning)]" />}
              count={schedules?.itemsWithSchedule ?? 0}
              countLabel="scheduled items"
              onClick={() => navigate('/metadata/schedules')}
            />
          </div>

          <MetadataCrossConnectionsSection />
        </>
      )}
    </PageLayout>
  );
};

interface NavigationTileProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  count: number;
  countLabel: string;
  onClick: () => void;
}

const NavigationTile: React.FC<NavigationTileProps> = ({ title, description, icon, count, countLabel, onClick }) => (
  <Card
    isClickable
    isHoverable
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    }}
    className="flex flex-col items-start text-left"
  >
    <div className="mb-3">{icon}</div>
    <h3 className="font-semibold text-[var(--color-text-primary)]">{title}</h3>
    <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{description}</p>
    <p className="mt-3 text-lg font-bold text-[var(--color-text-primary)]">
      {count} <span className="text-sm font-normal text-[var(--color-text-secondary)]">{countLabel}</span>
    </p>
  </Card>
);

export default MetadataExplorerPage;
